<template>
    <b-modal
        :visible="show"
        @hidden="$emit('update:show', false)"
        :title="isCreateMode ? '조직경계 추가' : '조직경계 수정'"
        size="xl"
        centered
        hide-footer
        modal-class="boundary-modal-high-z"
    >
        <div class="boundary-edit-form">
            <!-- 경계 정보 -->
            <section class="form-section">
                <h4 class="section-title">조직경계 정보</h4>
                <div class="form-grid">
                    <div class="form-field">
                        <label class="form-label">경계명</label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="formData.orgBoundaryName"
                            placeholder="조직경계명 입력"
                        />
                    </div>
                </div>
            </section>

            <!-- 산정 방법 -->
            <section class="form-section">
                <h4 class="section-title">산정 방법</h4>
                <div class="form-grid">
                    <div class="form-field">
                        <label class="form-label">산정 방법</label>
                        <b-form-select
                            v-model="formData.accountingMethod"
                            :options="accountingMethodOptions"
                            class="form-control"
                        ></b-form-select>
                    </div>
                    <div class="form-field" v-if="formData.accountingMethod === 'EQUITY_SHARE'">
                        <label class="form-label">점유율 (%)</label>
                        <input
                            type="number"
                            class="form-control"
                            v-model.number="formData.sharePct"
                            min="0"
                            max="100"
                            placeholder="0-100"
                        />
                    </div>
                    <div class="form-field" v-else></div>
                </div>
            </section>

            <!-- 기간 정보 -->
            <section class="form-section">
                <h4 class="section-title">기간 정보</h4>
                <div class="form-grid">
                    <div class="form-field">
                        <label class="form-label">시작일</label>
                        <div class="date-picker-wrapper">
                            <date-picker
                                v-model="formData.startDate"
                                type="date"
                                format="YYYY-MM-DD"
                                value-type="format"
                                placeholder="시작일 선택"
                                :append-to-body="true"
                                :popup-style="{ zIndex: 10001 }"
                            ></date-picker>
                        </div>
                    </div>
                    <div class="form-field">
                        <label class="form-label">종료일</label>
                        <div class="date-picker-wrapper">
                            <date-picker
                                v-model="formData.endDate"
                                type="date"
                                format="YYYY-MM-DD"
                                value-type="format"
                                placeholder="종료일 선택"
                                :append-to-body="true"
                                :popup-style="{ zIndex: 10001 }"
                            ></date-picker>
                        </div>
                    </div>
                </div>
            </section>

            <!-- 추가 정보 -->
            <section class="form-section">
                <h4 class="section-title">추가 정보</h4>
                <div class="form-grid">
                    <div class="form-field full-span">
                        <label class="form-label">조직 범위</label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="formData.organizationScope"
                            placeholder="조직 범위 설명"
                        />
                    </div>
                    <div class="form-field full-span">
                        <label class="form-label">경계 설정 근거</label>
                        <textarea
                            class="form-control"
                            v-model="formData.boundaryRationale"
                            rows="3"
                            placeholder="경계 설정 근거 입력"
                        ></textarea>
                    </div>
                    <div class="form-field full-span">
                        <label class="form-label">비고</label>
                        <textarea
                            class="form-control"
                            v-model="formData.remarks"
                            rows="2"
                            placeholder="비고를 입력하세요"
                        ></textarea>
                    </div>
                </div>
            </section>

            <!-- 시설 매핑 -->
            <section class="form-section">
                <div class="section-header-with-action">
                    <h4 class="section-title">시설 매핑</h4>
                    <button class="btn-add-facility" @click="addFacility" type="button">
                        <i class="fas fa-plus"></i>
                        시설 추가
                    </button>
                </div>

                <div class="facilities-section">
                    <div v-if="!formData.facilities || formData.facilities.length === 0" class="empty-facilities">
                        <i class="fas fa-industry"></i>
                        <p>시설을 추가해주세요</p>
                    </div>

                    <div v-else class="facilities-list">
                        <div v-for="(facility, fIdx) in formData.facilities" :key="`fac-${fIdx}`" class="facility-item">
                            <div class="facility-header">
                                <span class="facility-number">시설 {{ fIdx + 1 }}</span>
                                <button
                                    class="btn-remove-facility"
                                    @click="removeFacility(fIdx)"
                                    type="button"
                                    title="삭제"
                                >
                                    <i class="fas fa-times"></i>
                                </button>
                            </div>
                            <div class="form-grid">
                                <div class="form-field full-span">
                                    <label class="form-label">시설 선택</label>
                                    <b-form-select
                                        :value="facility.facilityIdx"
                                        @change="onFacilitySelect(fIdx, $event)"
                                        :options="getFacilityOptions()"
                                        class="form-control"
                                    ></b-form-select>
                                </div>
                                <div class="form-field">
                                    <label class="form-label">지분율 (%)</label>
                                    <input
                                        type="number"
                                        class="form-control"
                                        v-model.number="facility.includePct"
                                        min="0"
                                        max="100"
                                        placeholder="100"
                                    />
                                </div>
                                <div class="form-field">
                                    <label class="form-label">비고</label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        v-model="facility.remarks"
                                        placeholder="비고 입력"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- 액션 버튼 -->
            <div class="modal-actions">
                <button class="btn btn-secondary" @click="$emit('update:show', false)">취소</button>
                <button class="btn btn-primary" @click="save" :disabled="saving">
                    <i class="fas fa-spinner fa-spin" v-if="saving"></i>
                    {{ saving ? "저장 중..." : "저장" }}
                </button>
            </div>
        </div>
    </b-modal>
</template>

<script>
import carbonApi from "@/submodules/carbon-plugin/api";
import { unwrapSuccess, extractErrorMessage } from "../../../utils/api.js";
import CarbonCodes from "../../../codes";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

export default {
    name: "BoundaryOrgBoundaryEditModal",
    components: {
        DatePicker,
    },
    props: {
        show: {
            type: Boolean,
            required: true,
        },
        boundaryData: {
            type: Object,
            required: true,
        },
        orgCode: {
            type: String,
            default: null,
        },
    },
    computed: {
        isCreateMode() {
            return !this.boundaryData.orgBoundaryIdx && !this.boundaryData.boundaryIdx;
        },
        accountingMethodOptions() {
            return CarbonCodes.accountingMethod.getList();
        },
    },
    data() {
        return {
            saving: false,
            availableFacilities: [], // 선택 가능한 시설 목록

            formData: {
                orgBoundaryName: "",
                accountingMethod: "OPERATIONAL_CONTROL",
                sharePct: 100,
                startDate: null,
                endDate: null,
                organizationScope: "",
                boundaryRationale: "",
                remarks: "",
                facilities: [], // 시설 매핑
            },
        };
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.loadAvailableFacilities();
                this.loadFormData();
            }
        },
        boundaryData: {
            handler(newVal, oldVal) {
                if (!this.show) return;
                if (newVal === oldVal) return;
                this.loadFormData();
            },
            deep: false,
        },
    },
    mounted() {
        if (this.show) {
            this.loadAvailableFacilities();
            this.loadFormData();
        }
    },
    methods: {
        async loadAvailableFacilities() {
            try {
                const response = await carbonApi.utility.getFacilities();
                const responseData = response?.data?.data || response?.data;
                this.availableFacilities = responseData?.facilities || [];
            } catch (error) {
                console.error("[BoundaryOrgBoundaryEditModal] 시설 목록 로드 실패:", error);
                this.availableFacilities = [];
            }
        },

        async loadFormData() {
            if (!this.boundaryData) return;

            // 기본 정보 로드
            this.formData = {
                orgBoundaryName: this.boundaryData.orgBoundaryName || "",
                accountingMethod: this.boundaryData.accountingMethod || "OPERATIONAL_CONTROL",
                sharePct: this.boundaryData.sharePct || 100,
                startDate: this.boundaryData.startDate || null,
                endDate: this.boundaryData.endDate || null,
                organizationScope: this.boundaryData.organizationScope || "",
                boundaryRationale: this.boundaryData.boundaryRationale || "",
                remarks: this.boundaryData.remarks || "",
                facilities: [],
            };

            // 시설 매핑 로드 (수정 모드일 때만)
            if (!this.isCreateMode && this.boundaryData.orgBoundaryIdx) {
                try {
                    const response = await carbonApi.orgBoundary.getFacilities(this.boundaryData.orgBoundaryIdx);
                    const responseData = response?.data?.data || response?.data;
                    this.formData.facilities = responseData?.facilities || [];
                } catch (error) {
                    console.error("[BoundaryOrgBoundaryEditModal] 시설 매핑 로드 실패:", error);
                    this.formData.facilities = [];
                }
            }
        },

        addFacility() {
            if (!this.formData.facilities) {
                this.$set(this.formData, "facilities", []);
            }
            this.formData.facilities.push({
                facilityIdx: null,
                facilityCode: "",
                facilityName: "",
                includePct: 100,
                remarks: "",
            });
        },

        removeFacility(fIdx) {
            this.formData.facilities.splice(fIdx, 1);
        },

        getFacilityOptions() {
            const options = [{ value: null, text: "시설 선택" }];
            this.availableFacilities.forEach((fac) => {
                options.push({
                    value: fac.facilityIdx,
                    text: `${fac.facilityName} (${fac.facilityCode})`,
                });
            });
            return options;
        },

        onFacilitySelect(fIdx, facilityIdx) {
            if (!facilityIdx) {
                this.$set(this.formData.facilities[fIdx], "facilityIdx", null);
                this.$set(this.formData.facilities[fIdx], "facilityCode", "");
                this.$set(this.formData.facilities[fIdx], "facilityName", "");
                return;
            }

            const selected = this.availableFacilities.find((f) => f.facilityIdx === parseInt(facilityIdx));
            if (selected) {
                this.$set(this.formData.facilities[fIdx], "facilityIdx", selected.facilityIdx);
                this.$set(this.formData.facilities[fIdx], "facilityCode", selected.facilityCode);
                this.$set(this.formData.facilities[fIdx], "facilityName", selected.facilityName);
            }
        },

        async save() {
            this.saving = true;

            try {
                const payload = {
                    orgBoundaryName: this.formData.orgBoundaryName || null,
                    accountingMethod: this.formData.accountingMethod || "OPERATIONAL_CONTROL",
                    startDate: this.formData.startDate || null,
                    endDate: this.formData.endDate || null,
                    organizationScope: this.formData.organizationScope || null,
                    boundaryRationale: this.formData.boundaryRationale || "",
                    remarks: this.formData.remarks || null,
                };

                if (this.formData.accountingMethod === "EQUITY_SHARE") {
                    payload.sharePct = parseFloat(this.formData.sharePct || 100);
                }

                let response;
                let boundaryIdx;

                if (this.isCreateMode) {
                    // 생성 모드
                    if (!this.orgCode) {
                        throw new Error("조직 코드가 필요합니다.");
                    }
                    response = await carbonApi.orgBoundary.createOrgBoundary(this.orgCode, payload);
                    const responseData = unwrapSuccess(response, "조직경계 생성에 실패했습니다.");
                    boundaryIdx = responseData?.orgBoundaryIdx || responseData?.boundaryIdx;

                    this.$bvToast.toast("조직경계가 추가되었습니다.", {
                        variant: "success",
                        title: "추가 완료",
                    });
                } else {
                    // 수정 모드
                    boundaryIdx = this.boundaryData.orgBoundaryIdx || this.boundaryData.boundaryIdx;
                    response = await carbonApi.orgBoundary.updateOrgBoundary(boundaryIdx, payload);
                    unwrapSuccess(response, "조직경계 수정에 실패했습니다.");

                    this.$bvToast.toast("조직경계가 수정되었습니다.", {
                        variant: "success",
                        title: "수정 완료",
                    });
                }

                // 시설 매핑 저장
                if (boundaryIdx && this.formData.facilities && this.formData.facilities.length > 0) {
                    const facilities = this.formData.facilities
                        .filter((f) => f && (f.facilityCode || f.facilityName))
                        .map((f) => ({
                            facilityCode: f.facilityCode || "",
                            facilityName: f.facilityName || "",
                            includePct: parseFloat(f.includePct || 100),
                            remarks: f.remarks || "",
                        }));

                    if (facilities.length > 0) {
                        await unwrapSuccess(
                            await carbonApi.orgBoundary.saveFacilities(boundaryIdx, { facilities }),
                            "시설 매핑 저장에 실패했습니다."
                        );
                    }
                }

                this.$emit("saved", { response });
                this.$emit("update:show", false);
            } catch (error) {
                console.error("[BoundaryOrgBoundaryEditModal] 조직경계 저장 실패:", error);

                const errorMsg = extractErrorMessage(error, "조직경계 저장에 실패했습니다.");
                this.$bvToast.toast(errorMsg, {
                    variant: "danger",
                    title: "저장 실패",
                });
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.boundary-edit-form {
    max-height: 75vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    padding: 8px;
}

.form-section {
    background: #ffffff;
    padding: 0;
    padding-bottom: 0;
    margin-bottom: 32px;
    transition: all 0.2s ease;
}

.form-section:last-child {
    margin-bottom: 0;
}

.section-title {
    font-size: 15px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.01em;
    margin-bottom: 12px;
}

.section-header-with-action {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.section-header-with-action .section-title {
    margin: 0;
    padding-bottom: 0;
    margin-bottom: 0;
    border-bottom: none;
}

.facilities-section {
    margin-top: 0;
}

/* 그리드 레이아웃 - 2열 */
.form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0 20px;
    margin-top: 0;
}

.form-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 4px 0;
}

.form-field.full-span {
    grid-column: 1 / -1;
}

.form-grid label {
    margin: 0px;
}

/* 라벨 스타일 */
.form-label {
    font-size: 12px;
    font-weight: 600;
    line-height: 1.3;
    color: #334155;
}

/* 입력 필드 스타일 */
.form-control {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    font-size: 13px;
    color: #0f172a;
    background: #ffffff;
    transition: all 0.2s ease;
    line-height: 1.5;
    font-family: inherit;
    height: 36px;
}

.form-control:focus {
    outline: none;
    border-color: #cbd5e1;
    background: #fafbfc;
}

.form-control::placeholder {
    color: #94a3b8;
    font-size: 12px;
    font-weight: 400;
}

textarea.form-control {
    resize: vertical;
    padding: 10px 12px;
    font-family: inherit;
    line-height: 1.6;
    height: auto;
    min-height: 80px;
}

/* DatePicker 스타일 */
.date-picker-wrapper {
    position: relative;
    width: 100%;
}

.date-picker-wrapper >>> .mx-input-wrapper {
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    font-size: 13px;
    width: 100%;
}

.date-picker-wrapper >>> .mx-input {
    border: none;
    font-size: 13px;
    padding: 8px 12px;
    height: 36px;
    width: 100%;
    background: #ffffff;
    color: #0f172a;
}

.date-picker-wrapper >>> .mx-input:focus {
    border: none;
    box-shadow: none;
    outline: none;
}

.date-picker-wrapper >>> .mx-input-wrapper:focus-within {
    border-color: #cbd5e1;
    background: #fafbfc;
}

.date-picker-wrapper >>> .mx-input::placeholder {
    color: #94a3b8;
    font-size: 12px;
}

/* DatePicker popup z-index 조정 */
.date-picker-wrapper >>> .mx-datepicker-popup {
    z-index: 10001 !important;
}

/* DatePicker input 클릭 가능하도록 */
.date-picker-wrapper >>> .mx-input-wrapper {
    cursor: pointer;
    pointer-events: auto;
}

.date-picker-wrapper >>> .mx-input {
    cursor: pointer;
    pointer-events: auto;
}

/* DatePicker 아이콘 클릭 가능하도록 */
.date-picker-wrapper >>> .mx-input-wrapper .mx-calendar-icon {
    cursor: pointer;
    pointer-events: auto;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 8px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
}

.btn {
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
}

.btn-secondary {
    background: #e2e8f0;
    color: #475569;
}

.btn-secondary:hover {
    background: #cbd5e1;
}

.btn-primary {
    background: #52b788;
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background: #2d8659;
}

.btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* 시설 추가 버튼 */
.btn-add-facility {
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
    color: #52b788;
    background: transparent;
    border: 1px solid #52b788;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-add-facility:hover {
    background: #52b788;
    color: white;
}

.btn-add-facility i {
    font-size: 10px;
    margin-right: 4px;
}

/* 빈 시설 상태 */
.empty-facilities {
    padding: 30px;
    text-align: center;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 6px;
    color: #94a3b8;
}

.empty-facilities i {
    font-size: 32px;
    margin-bottom: 8px;
}

.empty-facilities p {
    margin: 0;
    font-size: 13px;
}

/* 시설 목록 */
.facilities-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 16px;
}

/* 시설 항목 */
.facility-item {
    padding: 16px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
}

.facility-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f1f5f9;
}

.facility-number {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
}

.btn-remove-facility {
    width: 24px;
    height: 24px;
    padding: 0;
    background: transparent;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    color: #94a3b8;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-remove-facility:hover {
    background: #fee2e2;
    border-color: #ef4444;
    color: #ef4444;
}

.btn-remove-facility i {
    font-size: 10px;
}

/* 반응형 */
@media (max-width: 1200px) {
    .form-grid {
        grid-template-columns: 1fr;
        gap: 14px;
    }
}

@media (max-width: 768px) {
    .boundary-edit-form {
        gap: 16px;
        padding: 6px 2px 2px 0;
    }

    .form-field {
        padding: 14px 0;
    }

    .form-grid {
        grid-template-columns: 1fr;
    }
}
</style>
